import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Mail, Check } from 'lucide-react'
import UiButton from './ui/UiButton'
import { apiClient } from '../api/client'
import { trackEvent } from '../utils/metaPixel'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function NewsletterSignup({ compact = false }) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [consent, setConsent] = useState(false)
  const [status, setStatus] = useState('idle')
  const [message, setMessage] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage('')

    if (!email.trim()) {
      setMessage(t('newsletter.errorEmailRequired', 'Proszę podać adres e-mail.'))
      setStatus('error')
      return
    }

    if (!EMAIL_RE.test(email.trim())) {
      setMessage(t('newsletter.errorInvalidEmail', 'Proszę podać prawidłowy adres e-mail.'))
      setStatus('error')
      return
    }

    if (!consent) {
      setMessage(t('newsletter.errorConsentRequired', 'Proszę wyrazić zgodę na kontakt.'))
      setStatus('error')
      return
    }

    setStatus('submitting')

    try {
      await apiClient('/newsletter', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), consent: true }),
      })
      trackEvent('Lead')
      setStatus('success')
      setMessage(t('newsletter.success', 'Dziękujemy! Skontaktujemy się z Tobą wkrótce.'))
      setEmail('')
      setConsent(false)
    } catch (err) {
      setStatus('error')
      if (err.status === 503) {
        setMessage(t('newsletter.errorNotConfigured', 'Usługa newslettera nie jest skonfigurowana. Skontaktuj się z administratorem.'))
      } else if (err.status === 502) {
        setMessage(t('newsletter.errorConnection', 'Błąd połączenia z usługą newslettera. Spróbuj ponownie później.'))
      } else {
        setMessage(err.message || t('newsletter.errorGeneric', 'Coś poszło nie tak. Spróbuj ponownie później.'))
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-3 ${compact ? '' : 'max-w-md mx-auto'}`}>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-blue/50" size={18} />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('newsletter.placeholder', 'Twój adres e-mail')}
            disabled={status === 'submitting'}
            className="w-full pl-10 pr-4 py-3 rounded-full bg-white/90 border border-brand-blue/10 text-brand-blue placeholder:text-brand-blue/40 focus:outline-none focus:ring-2 focus:ring-brand-gold disabled:opacity-60"
          />
        </div>
        <UiButton
          type="submit"
          variant="gold"
          disabled={status === 'submitting'}
          icon={status === 'submitting' ? Loader2 : Check}
          className="whitespace-nowrap"
        >
          {status === 'submitting'
            ? t('newsletter.submitting', 'Zapisywanie...')
            : t('newsletter.submit', 'Zapisz się')}
        </UiButton>
      </div>

      <label className="flex items-start gap-2 text-sm text-brand-blue/80 cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={status === 'submitting'}
          className="mt-0.5 rounded border-brand-blue/30 text-brand-orange focus:ring-brand-orange"
        />
        <span>{t('newsletter.consent', 'Wyrażam zgodę na kontakt mailowy w sprawie newslettera.')}</span>
      </label>

      {message && (
        <p
          className={`text-sm ${
            status === 'success' ? 'text-green-700' : 'text-red-700'
          }`}
        >
          {message}
        </p>
      )}
    </form>
  )
}
